/**
 * Widget key → component. The keys are the API's (src/config/dashboard-widgets.ts
 * in fieldiq-api); which widgets a company gets, in what order and for which
 * verticals is the API's answer, read by lib/overview-layout.ts.
 *
 * `scorecard` is retired (2026-10-03): the band carries its figures. A layout
 * that still names it renders nothing for it, the same as any key with no
 * entry here.
 *
 * activity_feed is registered (api#1147) ahead of its catalogue flip: until
 * the API resolves it into a layout it renders nothing. The phase 3 keys
 * (operations_rail, tomorrow_readiness) are deliberately absent: the API
 * never resolves them into a layout, and a key with no entry here renders
 * nothing. approved_vs_invoiced and unbilled_work
 * (api#1125) are registered: the catalogue decides who gets them.
 */
import type { ComponentType } from 'react';
import ActionQueueWidget from './widgets/ActionQueueWidget';
import JobBoardWidget from './widgets/JobBoardWidget';
import RevenuePulseWidget from './widgets/RevenuePulseWidget';
import TicketPipelineWidget from './widgets/TicketPipelineWidget';
import ReceivablesWidget from './widgets/ReceivablesWidget';
import QuotePipelineWidget from './widgets/QuotePipelineWidget';
import SafetyCertsWidget from './widgets/SafetyCertsWidget';
import EquipmentTrackerWidget from './widgets/EquipmentTrackerWidget';
import TruckingDispatchWidget from './widgets/TruckingDispatchWidget';
import RentalsSummaryWidget from './widgets/RentalsSummaryWidget';
import SalesPipelineWidget from './widgets/SalesPipelineWidget';
import MyFollowUpsWidget from './widgets/MyFollowUpsWidget';
import UnbilledWorkWidget from './widgets/UnbilledWorkWidget';
import ApprovedVsInvoicedWidget from './widgets/ApprovedVsInvoicedWidget';
import ToolsInHoleWidget from './widgets/ToolsInHoleWidget';
import WellsOnLocationWidget from './widgets/WellsOnLocationWidget';
import PtJobsWidget from './widgets/PtJobsWidget';
import WtJobsWidget from './widgets/WtJobsWidget';
import WsJobsWidget from './widgets/WsJobsWidget';
import WorkOrdersWidget from './widgets/WorkOrdersWidget';
import NdtComplianceWidget from './widgets/NdtComplianceWidget';
import ActivityFeedWidget from './widgets/ActivityFeedWidget';

export const WIDGET_REGISTRY: Readonly<Record<string, ComponentType>> = {
  action_queue: ActionQueueWidget,
  job_board: JobBoardWidget,
  revenue_pulse: RevenuePulseWidget,
  ticket_pipeline: TicketPipelineWidget,
  receivables: ReceivablesWidget,
  quote_pipeline: QuotePipelineWidget,
  safety_certs: SafetyCertsWidget,
  equipment_tracker: EquipmentTrackerWidget,
  trucking_dispatch: TruckingDispatchWidget,
  rentals_summary: RentalsSummaryWidget,
  // CRM (api#1064): every vertical, default off; an admin adds it in Settings.
  sales_pipeline: SalesPipelineWidget,
  my_follow_ups: MyFollowUpsWidget,
  // Global money (api#1125): every vertical, per currency.
  unbilled_work: UnbilledWorkWidget,
  approved_vs_invoiced: ApprovedVsInvoicedWidget,
  // Vertical operations (api#1141): the catalogue gates each by vertical and role.
  tools_in_hole: ToolsInHoleWidget,
  wells_on_location: WellsOnLocationWidget,
  pt_jobs: PtJobsWidget,
  // Well testing (api#1261, WT-6): the catalogue gates it by vertical and role.
  wt_jobs: WtJobsWidget,
  // Well servicing (api#1296, WS-6): the catalogue gates it by vertical and role.
  ws_jobs: WsJobsWidget,
  work_orders: WorkOrdersWidget,
  // NDT (api#1142): global, data-gated; in the NDT default set.
  ndt_compliance: NdtComplianceWidget,
  activity_feed: ActivityFeedWidget,
};

export const isRegisteredWidget = (key: string): boolean =>
  Object.prototype.hasOwnProperty.call(WIDGET_REGISTRY, key);
